import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCcw, X } from "lucide-react";

function Section({ title, kind, items, label, restore, purge }) {
  return (
    <div className="card-surface" data-testid={`recycle-section-${kind}`}>
      <div className="p-6 border-b border-neutral-200">
        <h2 className="font-display text-xl font-semibold">
          {title}{" "}
          <span className="font-mono-num text-ink-muted text-base">({items.length})</span>
        </h2>
      </div>
      {items.length === 0 && (
        <div className="p-6 text-sm text-ink-muted">Nothing here.</div>
      )}
      <div>
        {items.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between px-6 py-3 border-b border-neutral-100 last:border-0"
            data-testid={`recycle-row-${kind}-${r.id}`}
          >
            <div>
              <div className="text-sm font-medium">{label(r)}</div>
              <div className="text-xs text-ink-muted">
                Deleted {(r.deleted_at || "").slice(0, 10)}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full border-neutral-200"
                onClick={() => restore(kind, r.id)}
                data-testid={`restore-${kind}-${r.id}`}
              >
                <RotateCcw size={12} strokeWidth={1.75} /> Restore
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full border-terracotta-100 text-terracotta-600 hover:bg-terracotta-100"
                onClick={() => purge(kind, r.id)}
                data-testid={`purge-${kind}-${r.id}`}
              >
                <X size={12} strokeWidth={1.75} /> Hide
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RecycleBin() {
  const [data, setData] = useState({ bills: [], buyers: [], employees: [] });

  const load = async () => {
    const r = await api.get("/recycle-bin");
    setData(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const restore = async (kind, id) => {
    try {
      await api.post(`/recycle-bin/${kind}/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const purge = async (kind, id) => {
    if (!window.confirm("Hide this from the Recycle Bin? It is kept in backups and the audit log, never erased.")) return;
    try {
      await api.delete(`/recycle-bin/${kind}/${id}`);
      toast.success("Hidden from Recycle Bin");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <div className="mb-8 animate-fade-up">
        <div className="text-[11px] uppercase tracking-[0.3em] text-ink-muted">
          Reversible
        </div>
        <h1 className="font-display text-5xl font-semibold tracking-tight mt-1">
          Recycle Bin
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Section
          title="Bills"
          kind="bills"
          items={data.bills}
          label={(r) => r.bill_number}
          restore={restore}
          purge={purge}
        />
        <Section
          title="Buyers"
          kind="buyers"
          items={data.buyers}
          label={(r) => r.name}
          restore={restore}
          purge={purge}
        />
        <Section
          title="Employees"
          kind="employees"
          items={data.employees}
          label={(r) => r.name}
          restore={restore}
          purge={purge}
        />
      </div>
    </div>
  );
}
